import { type ComponentChildren } from 'preact'
import { useEffect } from 'preact/hooks'
import { getApps } from 'firebase/app'

import codigoLogo from 'assets/icons/codigo-logo.svg'
import { initializeFirebase } from 'state/auth/initialize_firebase'
import { defaultTenantId, firebaseConfig } from 'config'
import { LinksActions, LocalStorageKeys } from 'types'
import { loadObjectFromLocalStorage, saveObjectToLocalStorage } from 'utils'

type Props = {
	children: ComponentChildren
}

const AuthLayout = ({ children }: Props) => {
	useEffect(() => {
		/**
		 * We need to check if a Firebase App exists.
		 * If it doesn't exist, we'll initialize it.
		 * This case can occur when the user logs out,
		 * so we should initialize Firebase.
		 *
		 * If a Firebase App exists is bc it was initiliazed
		 * by App with {@link initializeAuth}
		 */
		const firebaseNotInitialized = getApps().length === 0

		if (firebaseNotInitialized) {
			initializeFirebase(defaultTenantId, firebaseConfig)
		}
	}, [])

	/**
	 * Stores a pending collaborator invitation in localStorage when accessed from an invitation link.
	 *
	 * This effect runs once on component mount and checks if the URL contains a `project-id`
	 * and the `action=accept-team-invitation` parameter. If both exist, it saves the project ID
	 * in `localStorage` under the key `LocalStorageKeys.CollaboratorInvitationProjects`.
	 *
	 * This mechanism ensures that if a user is not authenticated when receiving an invitation,
	 * the project ID will be stored locally until the user successfully logs in.
	 *
	 * @effect
	 * @see {@link LocalStorageKeys.CollaboratorInvitationProjects} for storage key reference.
	 * @see {@link LinksActions.AcceptTeamInvitation} for link actions reference.
	 */
	useEffect(() => {
		const params = new URLSearchParams(window.location.search)
		const projectId = params.get('project-id')
		const action = params.get('action')

		if (projectId && action === LinksActions.AcceptTeamInvitation) {
			// Load existing projects from localStorage
			const storedProjects =
				loadObjectFromLocalStorage<Record<string, string>>(
					LocalStorageKeys.CollaboratorInvitationProjects
				) || {}

			// Add new projectId
			storedProjects[projectId] = projectId

			// Save updated data
			saveObjectToLocalStorage(
				LocalStorageKeys.CollaboratorInvitationProjects,
				storedProjects
			)
		}
	}, [])

	return (
		<div class='relative grid h-screen w-screen place-content-center overflow-hidden'>
			<div class='absolute top-5 flex w-full justify-center'>
				<img alt='Código logo' class='h-10' src={codigoLogo} />
			</div>

			{children}
		</div>
	)
}

export { AuthLayout }
